import React, { useState, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { ContextProviders } from './components/providers/ContextProviders';
import AppRoutes from './routes/AppRoutes';
import { useVersionChecker } from './hooks/useVersionChecker';
import { Capacitor } from '@capacitor/core';
import { UpdateNotification } from './components/common/UpdateNotification';
import { OfflineIndicator } from './components/OfflineIndicator';
import ScrollToTop from './components/navigation/ScrollToTop';
import { useBootState } from './hooks/useBootState';
import BootScreen from './components/boot/BootScreen';
import BootErrorScreen from './components/boot/BootErrorScreen';
import { useSwipeBackGesture } from './hooks/useSwipeBackGesture';
import { usePushNotificationNavigation } from './hooks/usePushNotificationNavigation';
import { useDeepLinkNavigation } from './hooks/useDeepLinkNavigation';
import { AdminWebVitalsOverlay } from './components/debug/AdminWebVitalsOverlay';
import { NativeKeyboardProvider } from './contexts/NativeKeyboardContext';
import NativeKeyboardDismissButton from './components/navigation/NativeKeyboardDismissButton';
import { CinemaThemeProvider } from './contexts/CinemaThemeContext';
import CinemaBackground from './components/common/CinemaBackground';
import MaintenanceScreen from './components/pages/MaintenanceScreen';
import { getAppConfig, waitForAppConfig } from '@watchlist/shared';

const AppContent: React.FC = () => {
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);

  const { latestVersion, forceReload } = useVersionChecker({
    checkInterval: 5 * 60 * 1000, // Check every 5 minutes
    enabled: !Capacitor.isNativePlatform(), // Disabled on native — updates ship via App Store, not hot-swap
    autoReload: false, // Don't auto-reload, show notification instead
    onUpdateDetected: () => {
      setShowUpdateNotification(true);
    },
  });

  // Enable iOS swipe-back gesture and hardware back button handling
  useSwipeBackGesture();

  // Enable push notification navigation handler
  usePushNotificationNavigation();

  // Enable deep link / universal link navigation handler
  useDeepLinkNavigation();

  return (
    <>
      <OfflineIndicator />
      <AdminWebVitalsOverlay />
      <AppRoutes />
      <UpdateNotification
        open={showUpdateNotification}
        onReload={forceReload}
        onClose={() => setShowUpdateNotification(false)}
        latestVersion={latestVersion}
      />
    </>
  );
};

const App: React.FC = () => {
  const { phase, isReady, hasError, retry, isNative } = useBootState();
  const [configReady, setConfigReady] = useState(false);

  useEffect(() => {
    waitForAppConfig().finally(() => setConfigReady(true));
  }, []);

  // On native platforms, show boot screen while booting.
  // Firebase (and RC fetch) complete before phase reaches 'ready', so no extra wait needed.
  if (isNative) {
    if (phase === 'splash' || phase === 'boot-screen') {
      return (
        <ThemeProvider>
          <BootScreen />
        </ThemeProvider>
      );
    }

    if (hasError) {
      return (
        <ThemeProvider>
          <BootErrorScreen onRetry={retry} />
        </ThemeProvider>
      );
    }
  }

  // Web only: wait for Remote Config before rendering so flags apply on first render.
  // On native this is skipped — boot sequence already ensures RC is done before 'ready'.
  if (!isNative && !configReady) return null;

  // Maintenance mode — intercepts all users before any routing or auth
  if (getAppConfig().appMaintenanceMode) {
    return (
      <ThemeProvider>
        <MaintenanceScreen />
      </ThemeProvider>
    );
  }

  // Boot complete (or web platform) - render main app
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ThemeProvider>
          <CinemaThemeProvider>
            <NativeKeyboardProvider>
              <AuthProvider>
                <ContextProviders>
                  <div data-testid="main-app" style={{ minHeight: '100vh' }}>
                    <CinemaBackground />
                    <ScrollToTop />
                    <AppContent />
                  </div>
                </ContextProviders>
              </AuthProvider>
              <NativeKeyboardDismissButton />
            </NativeKeyboardProvider>
          </CinemaThemeProvider>
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
};

export default App;
